import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import statementService from '../../services/statementService';
import clinicService from '../../services/clinicService';
import { useT } from '../../i18n/LanguageContext';
import { easternToday, serverNow } from './formatters';
import { blobErrorText, throttleAware, unwrap } from './responseSafety';
import {
  RECIPIENT_FILTERS,
  buildStatementsCsv,
  compareBy,
  csvFilename,
  csvWasEscaped,
} from './recipientDomain';
import { createObjectUrlRegistry, downloadBlob, writePdfPlaceholder } from './pdfLifecycle';
import ScanWaiting from './components/ScanWaiting';
import { ErrorState, useScanLoad } from './tabShared';
import RecipientView from './components/RecipientView';
import { buildRecipientAdvisories } from './recipientAdvisories';

export { AdvisoryStrip } from './components/RecipientView';

const fetchPreview = async () => unwrap(await statementService.getPreview(), 'a statement report');

function useRecheckOnReturn() {
  const [, setTick] = useState(0);
  useEffect(() => {
    const bump = () => setTick((t) => t + 1);
    document.addEventListener('visibilitychange', bump);
    window.addEventListener('focus', bump);
    return () => {
      document.removeEventListener('visibilitychange', bump);
      window.removeEventListener('focus', bump);
    };
  }, []);
}

/** Stable fetchers, so the loader above is not rebuilt - and the tab not reloaded - every render. */

export default function RecipientsTab({ onPolicyChanged } = {}) {
  const t = useT();
  const { data, receivedAt, loading, error, refreshError, refreshing, reload } = useScanLoad(
    fetchPreview,
    'Could not load statements.'
  );
  useRecheckOnReturn();
  const [search, setSearch] = useState('');
  /**
   * Biggest debt first, which is the order an A/R page is read in. The server returns the ledger in
   * its own order; this makes the ordering the reader's, and states it in the header rather than
   * leaving them to infer it.
   */
  const [sort, setSort] = useState({ key: 'totalOpen', dir: 'desc' });
  const [only, setOnly] = useState('all');
  // First click sorts a column descending for money (the interesting end) and ascending for a name;
  // clicking the same column again reverses it.
  const onSort = (key) =>
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: key === 'customerName' ? 'asc' : 'desc' }
    );
  const [expanded, setExpanded] = useState(null);
  const [pdfBusy, setPdfBusy] = useState([]);
  // A failed PDF render must not blow away the loaded table. `error` replaces the whole view;
  // reloading it costs another call against a route throttled to one request a minute.
  const [pdfError, setPdfError] = useState('');
  /**
   * SOMETHING WORTH KNOWING IS NOT A FAILURE.
   *
   * "37 characters could not be printed" is reported after a statement rendered SUCCESSFULLY and is
   * now open in the next tab; it shared the red slot with "could not render that statement", so the
   * two were indistinguishable and a document that did open looked like one that had not.
   */
  const [pdfNotice, setPdfNotice] = useState('');
  // Object URLs are revoked when this component goes away, not on a wall-clock timer. Logout in
  // this app is a state change, not a document reload, so a timer-based revoke leaves another
  // customer's statement resolvable in the same tab after the session has ended.
  const blobUrls = useRef(null);
  if (!blobUrls.current) blobUrls.current = createObjectUrlRegistry();

  useEffect(() => () => blobUrls.current.revokeAll(), []);

  /**
   * A reload also clears what the PDF buttons had to say.
   *
   * Both notices are ABOUT the data being replaced - "these figures are from yesterday", "that
   * statement could not be rendered" - so leaving them up after a successful refresh told the
   * operator to refresh a page they had just refreshed, with nothing left on screen to explain it.
   */
  const load = useCallback(() => {
    setPdfError('');
    setPdfNotice('');
    /**
     * The optimistic overlay dies with the read it was layered on.
     *
     * It only ever forces `true`, so keeping it across a refresh would let a confirmation that did
     * not actually stick outrank the server's fresh answer - permanently, since the refresh is the
     * one thing that could have reconciled them. And the messages describe rows that are about to
     * be replaced.
     */
    setRuled({});
    setPauses({});
    setRulingFailures({});
    setRulingNotice('');
    // The export notice names a row count and a filename built from the ledger date, so it is at
    // least as data-bound as the two above it. Leaving it up after a reload described a file that
    // was cut from figures no longer on screen.
    setExportNotice('');
    return reload();
  }, [reload]);

  /**
   * Confirm one customer as a real practice.
   *
   * The same PUT the Clinics page makes, so there is one endpoint deciding this and one audit
   * record of who decided it. Only confirming is offered here: HIDING a customer removes it from
   * every order and IVR picker, and a consequence that large belongs on the screen that shows it.
   */
  /**
   * Pause or resume weekly statements for one customer (Andrew, 8/31/26).
   *
   * The reason is usually legal - collections, or active litigation - so the failure that matters
   * is a row that SAYS paused while the server still has them mailable. The update is rolled back
   * on any error and the customer is named, exactly as the practice ruling does.
   */
  const setPaused = async (row, next) => {
    if (!row.clinicId || pauseBusy.includes(row.qbCustomerId)) return;
    // Same stale-read guard as the practice ruling: this is a `$set` with no compare-and-swap, and
    // the tab is expected to sit open for days.
    if (staleSince()) {
      setRulingNotice('');
      setRulingFailures((f) => ({
        ...f,
        [row.qbCustomerId]: t(
          '{name}: these figures are from {asOf}. Refresh before pausing or resuming - someone may have changed this record since.',
          { name: row.customerName, asOf: data.asOf }
        ),
      }));
      return;
    }
    setRulingFailures((f) => {
      if (!f[row.qbCustomerId]) return f;
      const copy = { ...f };
      delete copy[row.qbCustomerId];
      return copy;
    });
    setPauses((p) => ({ ...p, [row.qbCustomerId]: next }));
    setPauseBusy((b) => [...b, row.qbCustomerId]);
    try {
      await clinicService.setStatementsPaused(row.clinicId, next);
      setRulingNotice(
        t(
          next
            ? '{name} paused. They stay on this list and will not be sent a statement.'
            : '{name} resumed. They will be sent a statement again.',
          { name: row.customerName }
        )
      );
    } catch (e) {
      // Put it back. A row reading "Paused" over a customer the server will still mail is the one
      // outcome worse than not offering the control.
      setPauses((p) => {
        const copy = { ...p };
        delete copy[row.qbCustomerId];
        return copy;
      });
      setRulingNotice('');
      setRulingFailures((f) => ({
        ...f,
        [row.qbCustomerId]: t(
          next ? '{name} could not be paused: {reason}' : '{name} could not be resumed: {reason}',
          { name: row.customerName, reason: t(throttleAware(e, 'the change was not saved')) }
        ),
      }));
    } finally {
      // A cached Rules tab must not keep advertising the old recipient list after a policy write.
      // Invalidate on an uncertain write response too; a failed response does not prove rollback.
      onPolicyChanged?.();
      setPauseBusy((b) => b.filter((id) => id !== row.qbCustomerId));
    }
  };

  const confirmPractice = async (row) => {
    if (!row.clinicId) return;
    /**
     * A STALE PAGE MUST NOT MAKE THIS WRITE.
     *
     * The ruling is a `$set` with no compare-and-swap, and this tab is expected to sit open for
     * days. If somebody marked this customer Hidden on the Clinics page since the read, the row
     * here still says Unreviewed and one click would flip a deliberate negative back to true,
     * re-admitting a bank row into the mailing population and every order picker. The page already
     * refuses a stale READ for a far smaller consequence.
     */
    if (staleSince()) {
      setRulingNotice('');
      setRulingFailures((f) => ({
        ...f,
        [row.qbCustomerId]: t(
          '{name}: these figures are from {asOf}. Refresh before ruling on a customer - someone may have changed this record since.',
          { name: row.customerName, asOf: data.asOf }
        ),
      }));
      return;
    }
    setRulingFailures((f) => {
      if (!f[row.qbCustomerId]) return f;
      const next = { ...f };
      delete next[row.qbCustomerId];
      return next;
    });
    // The row updates first and the control goes with it, which is also what stops a second click:
    // there is nothing left to press. A busy state would be unreachable.
    setRuled((r) => ({ ...r, [row.qbCustomerId]: true }));
    try {
      await clinicService.setPractice(row.clinicId, true);
      // SAID, not merely done. The badge vanishing is not an acknowledgement, and this writes the
      // flag that decides whether a customer can be emailed.
      setRulingNotice(t('{name} confirmed as a practice.', { name: row.customerName }));
    } catch (e) {
      // Put it back. A row that stays confirmed on screen after the write failed is the one
      // outcome worse than not offering the action at all.
      setRuled((r) => {
        const next = { ...r };
        delete next[row.qbCustomerId];
        return next;
      });
      setRulingNotice('');
      setRulingFailures((f) => ({
        ...f,
        [row.qbCustomerId]: t('{name} could not be confirmed: {reason}', {
          name: row.customerName,
          reason: t(throttleAware(e, 'the change was not saved')),
        }),
      }));
    }
  };

  /**
   * HAS THE EASTERN DAY MOVED ON SINCE THIS TABLE WAS READ?
   *
   * `asOf` is fixed at the moment of the read, and the ledger it describes is not. Everything below
   * that matters - which bucket an invoice sits in, how many days past due it is, whether a
   * statement may be rendered at all - is measured from that date, so a page carried across
   * midnight Eastern is quietly describing yesterday.
   *
   * A FUNCTION, NOT A RENDER-TIME VALUE. Computed once per render it was wrong in exactly the case
   * it exists for: a page left untouched overnight never re-renders, so the click handler still
   * closed over "not stale", the doomed request went out anyway, and it spent the route's
   * once-a-minute allowance on the way - which then blocked the refresh that would have fixed it.
   * Callers ask at the moment they need the answer.
   */
  const staleSince = useCallback(() => {
    const today = easternToday(serverNow(data?.generatedAt, receivedAt));
    return today && data?.asOf && data.asOf < today ? today : null;
  }, [data, receivedAt]);
  const staleToday = staleSince();

  /** A saved recipient edit updates this row without another throttled company-wide QuickBooks read. */
  const saveStatementCc = async (row, statementCcEmails) => {
    if (!row.clinicId) throw new Error('This customer has no clinic record.');
    if (staleSince()) throw new Error('Refresh the statements before changing recipients.');
    try {
      const response = await clinicService.setStatementCc(row.clinicId, statementCcEmails);
      const saved = response?.data?.data?.statementCcEmails;
      if (!Array.isArray(saved))
        throw new Error(
          'The server did not confirm the saved recipients. Refresh before editing again.'
        );
      setCcOverrides((current) => ({ ...current, [row.qbCustomerId]: saved }));
    } finally {
      // A failed response can still follow a committed write; force Rules to read fresh policy.
      onPolicyChanged?.();
    }
  };

  const [exportNotice, setExportNotice] = useState('');
  /**
   * Rulings made on this page since it loaded, keyed by QuickBooks customer id.
   *
   * OPTIMISTIC, AND NOT REFETCHED. The obvious implementation reloads after each confirmation -
   * which is a six-second walk of the entire company file, and the second one inside a minute comes
   * back 429 from the limiter. Clearing thirty-five customers would be impossible. So the row
   * updates locally and the server is told; a failure puts the row back and says why.
   */
  const [ruled, setRuled] = useState({});
  /**
   * Pauses changed on this page since it loaded, keyed by QuickBooks customer id.
   *
   * Optimistic for the same reason `ruled` is: re-reading is a six-second walk of the company file
   * and the second one inside a minute is refused by the limiter. The value is the NEW state, so
   * this overlay expresses both directions - unlike `ruled`, which only ever forces true.
   */
  const [pauses, setPauses] = useState({});
  const [ccOverrides, setCcOverrides] = useState({});
  // A successful ledger read replaces these local edits. Keep them if a refresh fails.
  useEffect(() => {
    setCcOverrides({});
  }, [receivedAt]);
  /**
   * Customers with a pause write in flight.
   *
   * The practice ruling needed nothing like this: confirming REMOVES its own control, so a second
   * click has nothing to press. This toggle stays put and flips, so a fast double-click fires
   * `true` then `false` - and if the responses land out of order the server keeps the first while
   * the screen shows the second. On the flag that decides whether an account in litigation gets
   * written to, "the screen says resumed and the server says paused" is not a state worth risking
   * for the sake of a control that is already instant.
   */
  const [pauseBusy, setPauseBusy] = useState([]);
  /** Failures keyed by customer. One slot cleared on the next attempt lost the first name before
   *  anyone had scrolled up to read it, and put that row back with no mark on it. */
  const [rulingFailures, setRulingFailures] = useState({});
  const [rulingNotice, setRulingNotice] = useState('');

  /** Has the reader narrowed the list? Both the totals row and the export turn on this. */
  const narrowed = Boolean(search.trim()) || only !== 'all';

  /**
   * STILL OUTSTANDING, counting what has been confirmed on this page.
   *
   * The server's tally is computed over the whole target set at read time. Left alone it kept saying
   * "35 have never been confirmed" after all thirty-five had been - and the sentence beside it sends
   * the reader to a filter that by then returns nobody. A banner and the table under it disagreeing
   * about the same number is the one contradiction the rest of this page refuses to make.
   */
  const outstandingUnconfirmed = (() => {
    const server = data?.unconfirmedPractices;
    if (!server) return { count: 0, totalOpen: 0 };
    const done = (data.statements || []).filter((st) => ruled[st.qbCustomerId]);
    return {
      count: Math.max(server.count - done.length, 0),
      totalOpen: Math.max(
        Math.round(
          (server.totalOpen - done.reduce((sum, st) => sum + (st.totalOpen || 0), 0)) * 100
        ) / 100,
        0
      ),
    };
  })();

  /**
   * An export notice describes a file cut from a particular list. The moment that list changes the
   * sentence stops being true - "Exported 37 customers" sitting above a three-row table, or "the
   * rows currently shown" above the whole list - so it is dropped whenever the narrowing moves.
   */
  useEffect(() => {
    setExportNotice('');
  }, [search, only, ruled]);

  /**
   * Download exactly what is on screen.
   *
   * Built from data already loaded, so it costs no request - which matters on a route capped at one
   * company-file walk a minute. It follows the search, the filter and the sort order, because that
   * is what the operator is looking at and what they mean by "this list".
   *
   * The object URL is revoked immediately: unlike the statement PDFs, nothing else needs to resolve
   * it once the browser has taken the bytes.
   */
  const exportCsv = () => {
    const partial = Boolean(data?.truncated);
    const name = csvFilename(data?.asOf, { narrowed, partial });
    try {
      downloadBlob(new Blob([buildStatementsCsv(rows)], { type: 'text/csv;charset=utf-8' }), name);
    } catch (e) {
      // A silent no-op is the wrong failure. Every other action on this tab lands its failure
      // somewhere the operator can see it, and a download that never happened looks identical to
      // one that did - until somebody goes looking for the file.
      setExportNotice(t('The file could not be created. Please try again.'));
      return;
    }
    // Said on the page as well as in the filename, because "did that include everyone?" is the
    // question somebody asks a minute after the file lands in their downloads folder.
    const rewritten = rows.filter((r) => csvWasEscaped(r.customerName)).length;
    // NAMED, not just flagged. Two different narrowings produced the same filename and the same
    // sentence, so the second export changed nothing in the DOM - the status region never fired and
    // the operator got no acknowledgement at all on a page that works hard everywhere else to
    // answer "why did my click do nothing?".
    const scope = [
      only !== 'all' ? t(RECIPIENT_FILTERS.find((f) => f.key === only)?.label) : null,
      search.trim() ? t('matching "{search}"', { search: search.trim() }) : null,
    ]
      .filter(Boolean)
      .join(', ');
    const exported = t(
      rows.length === 1
        ? 'Exported {count} customer to {file}'
        : 'Exported {count} customers to {file}',
      { count: rows.length, file: name }
    );
    setExportNotice(
      [
        narrowed
          ? `${exported} ${t('- the rows currently shown ({scope}), not the whole list.', { scope })}`
          : `${exported}.`,
        partial ? t('The list itself was capped, so some customers are missing from it.') : null,
        rewritten
          ? t(
              rewritten === 1
                ? '{count} name starting with = + - or @ gained a leading apostrophe, so a spreadsheet cannot execute them as formulas - they will not match QuickBooks exactly.'
                : '{count} names starting with = + - or @ gained a leading apostrophe, so a spreadsheet cannot execute them as formulas - they will not match QuickBooks exactly.',
              { count: rewritten }
            )
          : null,
      ]
        .filter(Boolean)
        .join(' ')
    );
  };

  // Fetch authenticated PDF bytes into an object URL retained until this tab unmounts.
  const openPdf = async (row) => {
    // The PDF and visible ledger must share the same Eastern date; stale rows require a refresh.
    if (staleSince()) {
      setPdfError('');
      setPdfNotice(
        t(
          'Refresh before opening a statement - these figures are from {asOf}, so the document would not match this page.',
          { asOf: data.asOf }
        )
      );
      return;
    }
    // Open during the click gesture, then sever opener access before the asynchronous fetch.
    const win = window.open('', '_blank');
    if (win) win.opener = null;
    writePdfPlaceholder(win, row.customerName, {
      title: t('Generating statement...'),
      heading: t('Generating PDF'),
      detail: t('This can take 10-15 seconds.'),
    });
    setPdfBusy((b) => [...b, row.qbCustomerId]);
    setPdfError('');
    setPdfNotice('');
    try {
      const res = await statementService.getStatementPdf(row.qbCustomerId, data?.asOf);
      // Pin the MIME type so a response header cannot make the blob render as HTML.
      const url = blobUrls.current.create(new Blob([res.data], { type: 'application/pdf' }));
      const dropped = Number(res.headers?.['x-statement-characters-dropped'] || 0);
      if (dropped > 0) {
        setPdfNotice(
          t(
            "{count} character(s) could not be printed on {name}'s statement and were left out of the document.",
            { count: dropped, name: row.customerName }
          )
        );
      }
      if (win) win.location = url;
      else window.location.assign(url); // popup blocked - use this tab rather than lose the click
    } catch (e) {
      if (win) win.close();
      // Replace server throttle text with an app-authored message.
      setPdfError(
        e?.response?.status === 429
          ? throttleAware(e, '')
          : await blobErrorText(e, 'Could not render that statement.')
      );
    } finally {
      setPdfBusy((b) => b.filter((id) => id !== row.qbCustomerId));
    }
  };

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let all = data?.statements || [];
    if (q) {
      // Search both customer and destination mailbox.
      all = all.filter(
        (s) =>
          (s.customerName || '').toLowerCase().includes(q) ||
          (s.billingEmail || '').toLowerCase().includes(q)
      );
    }
    // Apply local rulings before filtering and sorting.
    all = all.map((st) => (ruled[st.qbCustomerId] ? { ...st, isPractice: true } : st));
    // Presence matters because false represents a local resume; avoid prototype-chain lookups.
    all = all.map((st) =>
      Object.prototype.hasOwnProperty.call(pauses, st.qbCustomerId)
        ? { ...st, statementsPaused: pauses[st.qbCustomerId] }
        : st
    );
    all = all.map((st) =>
      Object.prototype.hasOwnProperty.call(ccOverrides, st.qbCustomerId)
        ? { ...st, statementCcEmails: ccOverrides[st.qbCustomerId] }
        : st
    );

    const filter = RECIPIENT_FILTERS.find((f) => f.key === only);
    if (filter && filter.key !== 'all') all = all.filter(filter.match);

    // Sort a copy so state remains immutable.
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...all].sort((a, b) => compareBy(a, b, sort.key) * dir);
  }, [data, search, only, sort, ruled, pauses, ccOverrides]);

  // Use server-wide totals for the unfiltered view and row totals for a narrowed result set.
  const { totals, totalsLabel } = useMemo(() => {
    // Invalid amounts contribute zero instead of coercing the accumulator into a string.
    const num = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
    const server = data?.agingTotals;
    const count = typeof data?.count === 'number' ? data.count : null;

    if (server && !narrowed && count !== null) {
      return {
        totals: server,
        totalsLabel: t(count === 1 ? 'Total of {count} customer' : 'Total of {count} customers', {
          count,
        }),
      };
    }
    return {
      totals: rows.reduce(
        (acc, x) => ({
          current: acc.current + num(x.aging?.current),
          d1_30: acc.d1_30 + num(x.aging?.d1_30),
          d31_60: acc.d31_60 + num(x.aging?.d31_60),
          d61_90: acc.d61_90 + num(x.aging?.d61_90),
          d90_plus: acc.d90_plus + num(x.aging?.d90_plus),
          totalOpen: acc.totalOpen + num(x.totalOpen),
        }),
        { current: 0, d1_30: 0, d31_60: 0, d61_90: 0, d90_plus: 0, totalOpen: 0 }
      ),
      totalsLabel: t('Total of {count} shown', { count: rows.length }),
    };
  }, [data, rows, narrowed, t]);

  if (loading) {
    return (
      <ScanWaiting
        title={t('Generating statements...')}
        detail={t('This can take 5-10 seconds.')}
      />
    );
  }
  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!data) return null;

  const advisories = buildRecipientAdvisories({ data, outstandingUnconfirmed, t });

  return (
    <RecipientView
      {...{
        t,
        staleToday,
        data,
        load,
        refreshError,
        advisories,
        rulingFailures,
        search,
        setSearch,
        only,
        setOnly,
        ruled,
        outstandingUnconfirmed,
        exportCsv,
        exportNotice,
        refreshing,
        rows,
        sort,
        onSort,
        expanded,
        setExpanded,
        pdfBusy,
        pdfError,
        pdfNotice,
        openPdf,
        pauses,
        pauseBusy,
        setPaused,
        rulingNotice,
        confirmPractice,
        saveStatementCc,
        totals,
        totalsLabel,
        narrowed,
      }}
    />
  );
}
